import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { logger } from '../lib/logger';
import { favoritesAPI } from '../lib/api';
import { Navbar } from '../components/layout/Navbar';
import { RecipeCard } from '../components/recipes/RecipeCard';
import { Button } from '../components/ui/button';
import { Heart, ChevronLeft, Utensils } from 'lucide-react';

export default function FavoritesPage() {
  const { isAuthenticated } = useAuth();
  const [favorites, setFavorites] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchFavorites = async () => {
      if (!isAuthenticated) {
        setLoading(false);
        return;
      }

      try {
        const response = await favoritesAPI.getAll();
        setFavorites(response.data);
      } catch (error) {
        logger.error('Failed to fetch favorites:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchFavorites();
  }, [isAuthenticated]);

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-worldplate-cream">
        <Navbar />
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 text-center">
          <div className="w-20 h-20 bg-worldplate-subtle rounded-full flex items-center justify-center mx-auto mb-6">
            <Heart className="w-10 h-10 text-worldplate-muted" />
          </div>
          <h1 className="font-heading text-3xl font-bold text-worldplate-deep mb-4">
            Connectez-vous pour voir vos favoris
          </h1>
          <p className="text-worldplate-muted mb-8">
            Gardez vos recettes préférées à portée de main
          </p>
          <Link to="/login">
            <Button className="bg-worldplate-primary hover:bg-worldplate-primary/90 text-white rounded-full px-8" data-testid="login-btn">
              Se connecter
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-worldplate-cream">
      <Navbar />

      {/* Header */}
      <section className="bg-worldplate-deep py-12 md:py-16" data-testid="favorites-header">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Link to="/">
            <Button variant="ghost" className="text-white hover:bg-white/20 mb-4 -ml-2" data-testid="back-btn">
              <ChevronLeft className="w-5 h-5 mr-1" />
              Retour
            </Button>
          </Link>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="flex items-center gap-4"
          >
            <div className="w-14 h-14 bg-worldplate-primary rounded-xl flex items-center justify-center">
              <Heart className="w-7 h-7 text-white" />
            </div>
            <div>
              <h1 className="font-heading text-4xl md:text-5xl font-bold text-white">
                Mes favoris
              </h1>
              <p className="text-white/70 mt-1">
                {favorites.length} recette{favorites.length !== 1 ? 's' : ''} sauvegardée{favorites.length !== 1 ? 's' : ''}
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Favorites Grid */}
      <section className="py-12 md:py-16" data-testid="favorites-grid">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="aspect-[4/3] rounded-2xl bg-stone-200 animate-pulse" />
              ))}
            </div>
          ) : favorites.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {favorites.map((recipe, index) => (
                <RecipeCard key={recipe.id} recipe={recipe} index={index} />
              ))}
            </div>
          ) : (
            <div className="text-center py-16">
              <div className="w-20 h-20 bg-worldplate-subtle rounded-full flex items-center justify-center mx-auto mb-6">
                <Utensils className="w-10 h-10 text-worldplate-muted" />
              </div>
              <h3 className="font-heading text-2xl font-semibold text-worldplate-deep mb-2">
                Aucun favori pour l'instant
              </h3>
              <p className="text-worldplate-muted mb-6">
                Explorez les recettes et ajoutez-les à vos favoris !
              </p>
              <Link to="/search">
                <Button className="bg-worldplate-primary hover:bg-worldplate-primary/90 text-white rounded-full px-6" data-testid="explore-recipes-btn">
                  Explorer les recettes
                </Button>
              </Link>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
